<template>
    <div class="mail-interface" :class="{ 'normal-mode': !isGridMode }">
        <MailSidebar
            :selected-folder="selectedFolder"
            :selected-email-id="selectedEmailId"
            :user-emails="userEmails"
            :department-emails="departmentEmails"
            :email-addresses="allowedSenderMailAddresses"
            :is-grid-mode="isGridMode"
            :show-only-folders="showOnlyFolders"
            @folder-selected="selectFolder"
            @email-selected="selectMailAccount"
            @create-new-mail="createNewMail"
            @layout-mode-changed="toggleLayout"
            @change-mail-type="changedMailType"
            ref="sidebar"
        />

        <template v-if="isGridMode">
            <MailList
                ref="mailList"
                :ref-visitor-id="refVisitorId"
                :selected-thread-id="selectedThread && selectedThread._id"
                :folder="selectedFolder"
                :mail-type="selectedEmailType"
                :selected-thread="selectedThread"
                @thread-selected="selectThread"
                :single-mode="mode === 'folder-only'"
                class="mail-list-grid"
            />

            <div class="mail-detail-container">
                <MailThread
                    v-if="showMailThread"
                    :thread="selectedThread"
                    @close="closeMailThread"
                    @thread-deleted="onThreadDeleted"
                />

                <MailCompose
                    v-if="showMailCompose"
                    :mail-data="composeMailData"
                    @close="closeComposeForm"
                    @sent="onMailSent"
                    :selected-email-id="selectedEmailId"
                    :enabled-select-sender="enabledSelectSender"
                />

                <div v-if="!showMailThread && !showMailCompose" class="empty-state">
                    <div class="empty-state-icon">
                        <i class="material-icons">email</i>
                    </div>
                    <div class="empty-state-message">{{ $t("mail.emptyStateMessage") }}</div>
                </div>
            </div>
        </template>

        <template v-else>
            <MailList
                v-if="!showMailThread && !showMailCompose"
                ref="mailList"
                :ref-visitor-id="refVisitorId"
                :selected-thread-id="selectedThread && selectedThread._id"
                :folder="selectedFolder"
                :mail-type="selectedEmailType"
                :is-grid-mode="isGridMode"
                @thread-selected="selectThread"
                :single-mode="mode === 'folder-only'"
                class="mail-list-normal"
            />

            <div class="mail-detail-container" v-if="showMailThread || showMailCompose">
                <MailThread
                    v-if="showMailThread"
                    :thread="selectedThread"
                    @close="closeMailThread"
                    @thread-deleted="onThreadDeleted"
                />
                <MailCompose
                    v-if="showMailCompose"
                    :mail-data="composeMailData"
                    @close="closeComposeForm"
                    :selected-email-id="selectedEmailId"
                    :enabled-select-sender="enabledSelectSender"
                />
            </div>
        </template>
    </div>
</template>

<script>
import MailThread from "./components/MailThread.vue";
import MailCompose from "./components/MailCompose.vue";
import MailSidebar from "./components/MailSidebar.vue";
import MailList from "./components/MailList.vue";
import { mapState, mapGetters, mapActions } from "vuex";

export default {
    name: "NewMailInterface",
    components: {
        MailThread,
        MailCompose,
        MailSidebar,
        MailList,
    },
    props: {
        mode: {
            type: String,
            default: "full",
            validator: (value) => ["full", "folder-only"].includes(value),
        },
        defaultMode: {
            type: String,
        },
        defaultSelectedFolder: {
            type: String,
            default: "inbox",
        },
        defaultSelectedEmailId: {
            type: String,
            default: "",
        },
        enabledSelectSender: {
            type: Boolean,
            default: false,
        },
        refVisitorId: {
            type: String,
            default: null,
        },
        visitorEmailAddress: {
            type: String,
            default: null,
        },
    },
    data() {
        return {
            selectedEmailType: "self",
            selectedFolder: this.defaultSelectedFolder,
            selectedEmailId: this.defaultSelectedEmailId,
            selectedThread: null,
            showMailThread: false,
            showMailCompose: false,
            composeMailData: {},
            cancels: [],
            isGridMode: !this.defaultMode || this.defaultMode === "grid",
        };
    },
    computed: {
        ...mapGetters({
            allowedMailAddresses: "mail/allowedMailAddresses",
            defaultMail: "mail/defaultMail",
        }),
        ...mapState({
            user: (state) => state.auth.user,
            allMailAddresses: (state) => state.mail.allMailAddresses,
        }),
        allowedSenderMailAddresses() {
            return this.allowedMailAddresses.filter((mail) => mail.isSenderMail ? mail.isSenderVerified : mail.isSender);
        },
        userEmails() {
            return this.allowedSenderMailAddresses.filter((mail) => mail.type === "user" && mail.user?._id !== this.user._id);
        },
        departmentEmails() {
            return this.allowedSenderMailAddresses.filter((mail) => mail.type === "department");
        },
        selectedEmail() {
            // Bu kısımı silmeyin, child componentlerde kullanılıyor
            return this.allowedSenderMailAddresses.find((mail) => mail._id === this.selectedEmailId);
        },
        showOnlyFolders() {
            return this.mode === "folder-only";
        },
    },
    created() {
        this.getMonthlyEmailCount();
        if (!this.defaultMode) {
            const savedLayoutMode = localStorage.getItem("mailLayoutMode");
            this.isGridMode = savedLayoutMode !== null ? savedLayoutMode === "grid" : true;
        }
    },
    mounted() {
        const urlParams = this.$route.query;

        if (urlParams.action === 'create') {
            const toEmail = urlParams.to || '';
            this.createNewMail(toEmail);
        }
    },
    beforeDestroy() {
        this.cancels.forEach((cancel) => cancel());
    },
    methods: {
        ...mapActions({
            getMonthlyEmailCount: "mail/getMonthlyEmailCount"
        }),
        changedMailType(type){
            this.selectedEmailType = type;
        },
        onThreadDeleted() {
            this.selectedThread = null;
            this.showMailThread = false;
            this.showMailCompose = false;
            this.$refs.mailList.refreshList();
        },

        getFirstMailAddress() {
            return this.defaultMail?._id || this.$_.get(this.allowedMailAddresses, "[0]._id", null);
        },

        toggleLayout(value) {
            this.isGridMode = value;

            if (!this.defaultMode) {
                localStorage.setItem("mailLayoutMode", value ? "grid" : "normal");
            }

            this.$emit("layout-mode-changed", value);
        },

        goBack() {
            if (this.showMailThread) {
                this.closeMailThread();
            } else if (this.showMailCompose) {
                this.closeComposeForm();
            }
        },

        selectFolder(folderId) {
            this.selectedFolder = folderId;

            this.$emit("folder-selected", folderId);

            this.closeMailDetailPanel();
        },

        selectMailAccount(emailId) {
            this.selectedEmailId = emailId;

            this.$emit("email-selected", emailId);

            this.closeMailDetailPanel();
        },

        selectThread(thread) {
            this.selectedThread = thread;
            this.showMailThread = true;
            this.showMailCompose = false;
        },

        createNewMail(toEmail = '') {
            this.composeMailData = {
                to: toEmail,
                cc: "",
                bcc: "",
                subject: "",
                body: "",
            };
            if (this.visitorEmailAddress) this.composeMailData.to = this.visitorEmailAddress;
            this.showMailCompose = true;
            this.selectedThread = null;
            this.showMailThread = false;
        },

        closeMailThread() {
            this.showMailThread = false;
            this.selectedThread = null;
        },

        closeComposeForm() {
            this.showMailCompose = false;
        },

        closeMailDetailPanel() {
            this.selectedThread = null;
            this.showMailThread = false;
            this.showMailCompose = false;
        },

        onMailSent() {
            this.$refs.mailList.refreshList();
        },
    },
    watch: {
        allowedMailAddresses: {
            deep: true,
            immediate: true,
            handler(newVal) {
                if (!this.defaultSelectedEmailId && !this.enabledSelectSender) {
                    const firstMailId = this.getFirstMailAddress();
                    this.selectedEmailId = firstMailId;
                    this.$emit("email-selected", firstMailId);
                }
            },
        },
        defaultSelectedEmailId: {
            deep: true,
            handler(newEmailId) {
                if (newEmailId) this.selectedEmailId = newEmailId;
            },
        },
    },
};
</script>

<style lang="scss" scoped>
.mail-interface {
    display: flex;
    height: 100%;
    width: 100%;
    position: relative;

    @keyframes slideIn {
        from {
            transform: translateX(100%);
            opacity: 0;
        }

        to {
            transform: translateX(0);
            opacity: 1;
        }
    }

    // Mail listesi - Grid modda
    .mail-list-grid {
        width: 380px;
        min-width: 380px;
        max-width: 380px;
    }

    // Mail listesi - Normal modda
    .mail-list-normal {
        flex: 1;
        width: auto;
        min-width: 0;
    }

    // Mail detay container
    .mail-detail-container {
        flex: 1;
        position: relative;
        background-color: #fff;
        overflow: auto;

        .back-button {
            display: flex;
            align-items: center;
            padding: 16px 20px;
            cursor: pointer;
            color: #444;
            border-bottom: 1px solid #eaeaea;

            i {
                font-size: 20px;
                margin-right: 8px;
            }

            span {
                font-size: 14px;
                font-weight: 500;
            }

            &:hover {
                background-color: #f9f9f9;
            }
        }

        .empty-state {
            display: flex;
            flex-direction: column;
            align-items: center;
            justify-content: center;
            height: 100%;

            .empty-state-icon {
                width: 80px;
                height: 80px;
                border-radius: 50%;
                background-color: #f5f5f5;
                display: flex;
                align-items: center;
                justify-content: center;
                margin-bottom: 16px;

                i {
                    font-size: 36px;
                    color: #999;
                }
            }

            .empty-state-message {
                font-size: 16px;
                color: #666;
                max-width: 280px;
                text-align: center;
                line-height: 1.5;
            }
        }
    }
}
</style>
